import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import { useStateWithLocalStoragePersistenceAndDefault } from "../common/hooks/useStateWithLocalStoragePersistenceAndDefault";
import { useBeamerBroadcastChannelReceiver } from "./useBeamerBroadcastChannelReceiver";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";
import { Fullscreen } from "./useFullscreen";
import { CyBeamer } from "./CyBeamer";
import { SwipeHandlers } from "../carousel/useSwipe";
import "./BeamerScreen.css";

interface Props {
  /** owned by the caller, so its own controls can show the screen fullscreen too */
  fullscreen: Fullscreen;
  /** navigation by touch gestures, where the screen controls the carousel */
  swipeHandlers?: SwipeHandlers;
  "data-cy"?: string;
}

/**
 * The projected screen, filling its parent. The font size is relative to the screen height (cqh),
 * so the beamer window, the smaller preview in the carousel and both in fullscreen show the same picture.
 */
export function BeamerScreen(props: Props) {
  const [fontSizeInViewHeight, setFontSizeInViewHeight]: [
    number,
    (value: ((prevState: number) => number) | number) => void,
  ] = useStateWithLocalStoragePersistenceAndDefault<number>(
    new LocalStorageKey("SongsheetBeamerView", "fontSizeInViewHeight"),
    8,
  );

  const { t } = useTranslation();
  const [content, setContent] = useState<string | null>(null);
  const defaultContent = t("beamer.defaultContent", {
    returnObjects: true,
  }).join("\n");
  useBeamerBroadcastChannelReceiver(setContent, setFontSizeInViewHeight);
  const { ref, isFullscreen, isFallbackFullscreen, toggleFullscreen } =
    props.fullscreen;
  const fullscreenLabel = isFullscreen
    ? t("beamer.exitFullscreen")
    : t("beamer.fullscreen");

  return (
    <div
      ref={ref}
      className={
        "beamer-screen" +
        (isFullscreen ? " beamer-screen-fullscreen" : "") +
        (isFallbackFullscreen ? " beamer-screen-fallback-fullscreen" : "")
      }
      //the gestures belong to the screen, the browser must neither scroll nor zoom
      style={props.swipeHandlers ? { touchAction: "none" } : undefined}
      {...props.swipeHandlers}
    >
      <div
        className="beamer-screen-content"
        data-cy={props["data-cy"]}
        style={{ fontSize: fontSizeInViewHeight + "cqh" }}
      >
        {content ?? defaultContent}
      </div>
      <button
        type="button"
        className="beamer-screen-fullscreen-button"
        onClick={toggleFullscreen}
        aria-label={fullscreenLabel}
        title={fullscreenLabel}
        data-cy={CyBeamer.FullscreenButton}
      >
        <FontAwesomeIcon
          icon={isFullscreen ? faCompress : faExpand}
          fixedWidth
        />
      </button>
    </div>
  );
}
